import type { LabelFormat } from '@/api/types/common';
import type { Shipment } from '@/api/types/shipments';

/**
 * Maps a label format to its file extension. Mirrors the backend
 * `LabelFormat::fileExtension()` - kept in sync because blob downloads can't
 * read the response's Content-Disposition cross-origin.
 */
export function labelFileExtension(format: LabelFormat | null): string {
  switch (format) {
    case 'application/pdf;format=A4':
    case 'application/pdf;format=A6':
      return 'pdf';
    case 'application/pdf+json;format=A4':
    case 'application/pdf+json;format=A6':
      return 'json';
    case 'text/zpl;dpi=203':
    case 'text/zpl;dpi=300':
      return 'zpl';
    case 'text/epl2;dpi=203':
      return 'epl';
    case 'text/dpl;dpi=203':
      return 'dpl';
    default:
      return 'pdf';
  }
}

/** Download name for a single label: `shipment-{carrier_shipment_id}.{ext}`. */
export function labelFilename(shipment: Shipment, format: LabelFormat): string {
  const identifier = shipment.carrier_shipment_id ?? String(shipment.id);
  return `shipment-${identifier}.${labelFileExtension(format)}`;
}

/** Download name for a multi-label archive: `{ext}_labels_{YYYY_MM_DD_HH_mm_ss}.zip`. */
export function labelArchiveFilename(format: LabelFormat, date: Date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  const stamp = [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
    pad(date.getHours()),
    pad(date.getMinutes()),
    pad(date.getSeconds()),
  ].join('_');
  return `${labelFileExtension(format)}_labels_${stamp}.zip`;
}
